import { getDueReminders } from "./api/reminderApi";
import { getCars } from "./api/carApi";
import { loadAllNextServiceMileages } from "./nextServiceMileage";
import {
  sortNotificationsDesc,
  normalizeDueResponse,
  mergeWithLocalNextServiceNotifications,
} from "../Page/Notifications/components/notificationUtils";

// Single source of truth for notification DATA (due service reminders).
// Reuses the exact data source as the /notifications page:
//   1. GET /reminders/due (backend due reminders ARE the notifications —
//      no Notification table / unread-read concept exists on the backend).
//   2. Frontend-only Next Service Mileage mirror (localStorage) merged in
//      the backend reminder shape for entries that are due but not yet
//      reflected in the backend response.
//
// Both the Header badge and the /notifications page consume this module,
// so the count shown in the Header is always the REAL notification count.
//
// In-flight de-duplication: concurrent callers (e.g. Header + page mounted
// together without a shared provider) share ONE underlying promise, so the
// new Header item never causes duplicate API requests.

let inflight = null;

async function loadNotificationsData() {
  try {
    const response = await getDueReminders();
    const backendList = normalizeDueResponse(response);
    if (backendList !== null) {
      // Frontend mirror: Next Service Mileage values saved at registration
      // (details JSON + localStorage) that are due but not yet reflected
      // in the backend response. Merged in the backend reminder shape.
      let merged = backendList;
      try {
        const localEntries = loadAllNextServiceMileages();
        if (localEntries.length > 0) {
          const vehiclesById = {};
          for (const r of backendList) {
            if (r?.vehicle?.id !== undefined) {
              vehiclesById[r.vehicle.id] = r.vehicle;
              vehiclesById[String(r.vehicle.id)] = r.vehicle;
            }
            if (r?.vehicleId !== undefined && r?.vehicle) {
              vehiclesById[r.vehicleId] = r.vehicle;
              vehiclesById[String(r.vehicleId)] = r.vehicle;
            }
          }
          try {
            const carsRes = await getCars();
            const cars = Array.isArray(carsRes?.data)
              ? carsRes.data
              : Array.isArray(carsRes)
                ? carsRes
                : [];
            for (const c of cars) {
              if (c?.id === undefined) continue;
              const v = {
                id: c.id,
                title: c.title || c.brand || "خودرو",
                currentMileage: c.currentMileage,
              };
              vehiclesById[c.id] = v;
              vehiclesById[String(c.id)] = v;
            }
          } catch {
            // Non-blocking: fall back to vehicles embedded in reminders.
          }
          merged = mergeWithLocalNextServiceNotifications({
            backendReminders: backendList,
            localEntries,
            vehiclesById,
          });
        }
      } catch {
        merged = backendList;
      }
      return sortNotificationsDesc(merged);
    }
    return [];
  } catch {
    // Backend unreachable: still surface due local Next Service Mileage
    // entries (with unknown mileage they stay hidden — no fake data).
    try {
      const localEntries = loadAllNextServiceMileages();
      if (localEntries.length > 0) {
        const merged = mergeWithLocalNextServiceNotifications({
          backendReminders: [],
          localEntries,
        });
        if (merged.length > 0) {
          return sortNotificationsDesc(merged);
        }
      }
    } catch {
      // fall through below
    }
    return [];
  }
}

export function fetchNotificationsData() {
  if (inflight) {
    return inflight;
  }
  inflight = loadNotificationsData().finally(() => {
    inflight = null;
  });
  return inflight;
}

// Test/maintenance hook: lets tests reset the shared in-flight promise.
export function __resetNotificationsInflight() {
  inflight = null;
}
